import { IOperatingHours } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { ServiceMode } from '@fhs-legacy/frontend/src/state/service-mode';
import { getFlagSync } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import {
  getDateNowByTimezone,
  isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours,
  isRestaurantOpen,
} from '@fhs-legacy/frontend/src/utils/restaurant';

import { PipelineBaseStep, usePaymentFlowStore } from '..';
import type { PipelineStepContext, PipelineStepResult } from '..';

export class CheckServiceModeStep extends PipelineBaseStep {
  // Validates that the service mode (takeout/delivery) is valid as restaurant is opened
  readonly name = 'CheckServiceModeStep';

  async execute(context: PipelineStepContext): Promise<PipelineStepResult> {
    usePaymentFlowStore.getState().setCurrentState('serviceModeCheck');

    const { storeId, serviceMode, fireOrderIn, restaurantHours, restaurantTimezone } = context;

    this.logger.log('Checking service mode availability', {
      storeId,
      serviceMode,
      fireOrderIn,
      hasRestaurantData: !!restaurantHours,
    });

    // Bypass check if future ordering is enabled (scheduled orders)
    // Future orders are validated separately based on their scheduled time
    const enableFutureOrdering = getFlagSync('enable-future-ordering');
    const isFutureOrder = fireOrderIn > 0;

    if (enableFutureOrdering && isFutureOrder) {
      this.logger.log('Bypassing service mode check for future order', { fireOrderIn });
      usePaymentFlowStore.getState().setCurrentState('idle');
      return this.success();
    }

    // Special handling for delivery with ENABLE_DELIVERY_CHECKOUT_OUTSIDE_OPENING_HOURS flag
    const enableDeliveryOutsideOpeningHours = getFlagSync(
      'enable-delivery-checkout-outside-opening-hours'
    );

    if (serviceMode === ServiceMode.DELIVERY && enableDeliveryOutsideOpeningHours) {
      this.logger.log('Bypassing service mode check for delivery (outside opening hours enabled)');
      usePaymentFlowStore.getState().setCurrentState('idle');
      return this.success();
    }

    // Check if we have restaurant hours data
    if (!restaurantHours) {
      return this.error(
        'MISSING_RESTAURANT_DATA',
        'Restaurant hours data not available',
        'Unable to verify restaurant availability. Please try again. Error Code: (No Restaurant Data)',
        'CheckServiceModeStep'
      );
    }

    try {
      // Get the appropriate hours for the current service mode
      const serviceModeHours = this.getServiceModeHours(serviceMode, restaurantHours);

      if (!serviceModeHours) {
        return this.error(
          'MISSING_SERVICE_MODE_HOURS',
          `No hours configured for ${serviceMode}`,
          'Unable to verify restaurant availability for this service mode. Please try a different service mode. Error Code: (No Hours Available)',
          'CheckServiceModeStep'
        );
      }

      // Get current time in the restaurant's timezone
      const dateNow = getDateNowByTimezone({
        timeZone: restaurantTimezone,
      });

      // Check if the restaurant is open for this service mode
      const restaurantIsOpen = isRestaurantOpen(serviceModeHours, dateNow);

      if (!restaurantIsOpen) {
        const serviceModeDisplayName = this.getServiceModeDisplayName(serviceMode);
        const userMessage = `This restaurant is currently closed for ${serviceModeDisplayName}. Please select a different service mode or try again later.`;

        return this.error(
          'SERVICE_MODE_UNAVAILABLE',
          `Restaurant is closed for ${serviceMode}`,
          userMessage,
          'CheckServiceModeStep'
        );
      }

      // Check if the restaurant is closing within the next 20 minutes
      // This prevents orders that might not be fulfilled before closing
      const isOpenAndNotClosingSoon = isOpenIfNotWithinMinutesOfCloseTodayAndNot24hours(
        serviceModeHours,
        dateNow,
        20
      );

      if (!isOpenAndNotClosingSoon) {
        const serviceModeDisplayName = this.getServiceModeDisplayName(serviceMode);
        const userMessage = `This restaurant is closing soon for ${serviceModeDisplayName}. Please select a different service mode or try again later.`;

        return this.error(
          'SERVICE_MODE_CLOSING_SOON',
          `Restaurant is closing within 20 minutes for ${serviceMode}`,
          userMessage,
          'CheckServiceModeStep'
        );
      }

      this.logger.log('Service mode check passed', {
        storeId,
        serviceMode,
        restaurantIsOpen,
        timezone: restaurantTimezone,
      });

      usePaymentFlowStore.getState().setCurrentState('idle');
      return this.success();
    } catch (error) {
      this.logger.logError('Error during service mode check', error);

      return this.error(
        'SERVICE_MODE_CHECK_ERROR',
        `Service mode check failed: ${error instanceof Error ? error.message : String(error)}`,
        'Unable to verify restaurant availability. Please try again. Error Code: (Service Mode Unavailable)',
        'CheckServiceModeStep'
      );
    }
  }

  // Gets the operating hours for the specified service mode
  private getServiceModeHours(
    serviceMode: ServiceMode,
    restaurantHours: NonNullable<PipelineStepContext['restaurantHours']>
  ): IOperatingHours | null | undefined {
    switch (serviceMode) {
      case ServiceMode.DELIVERY:
        return restaurantHours.deliveryHours;
      case ServiceMode.CURBSIDE:
        return restaurantHours.curbsideHours;
      case ServiceMode.DRIVE_THRU:
        return restaurantHours.driveThruHours;
      case ServiceMode.PICKUP_WINDOW:
        return restaurantHours.pickupWindowHours;
      case ServiceMode.TAKEOUT:
      case ServiceMode.EAT_IN:
      default:
        // TAKEOUT and EAT_IN use dining room hours
        return restaurantHours.diningRoomHours;
    }
  }

  private getServiceModeDisplayName(serviceMode: ServiceMode): string {
    const serviceModeNames: Record<ServiceMode, string> = {
      [ServiceMode.TAKEOUT]: 'pickup',
      [ServiceMode.DELIVERY]: 'delivery',
      [ServiceMode.CURBSIDE]: 'curbside',
      [ServiceMode.DRIVE_THRU]: 'drive-thru',
      [ServiceMode.EAT_IN]: 'dine-in',
      [ServiceMode.CATERING_PICKUP]: 'catering pickup',
      [ServiceMode.CATERING_DELIVERY]: 'catering delivery',
      [ServiceMode.PICKUP_WINDOW]: 'pickup window',
    };

    return serviceModeNames[serviceMode] || serviceMode.toLowerCase().replace(/_/g, ' ');
  }
}
